import {
  formatarTelefone,
  ROTULO_ETAPA,
  type AtendenteDTO,
  type ContatoDetalheDTO,
  type ContatoResumoDTO,
} from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Ban,
  Merge,
  MessageCircle,
  Pencil,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  AvatarContato,
  Modal,
  ROTULO_STATUS,
  StatusTexto,
  Vazio,
  useToast,
} from '../../componentes/ui';
import { api, patch, post } from '../../lib/api';
import { useContato, useContatos } from '../../lib/consultas';
import { quandoCurto } from '../../lib/tempo';
import { CadastroTiny } from '../../componentes/CadastroTiny';
import './contatos.css';

type Filtro = 'todos' | 'recorrentes' | 'bloqueados';

const ORIGEM = { whatsapp: 'WhatsApp', instagram: 'Instagram', anuncio: 'Anúncio' } as const;
const data = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function Contatos({ eu }: { eu: AtendenteDTO }) {
  const { id } = useParams();
  const [busca, setBusca] = useState('');
  const [buscaAtrasada, setBuscaAtrasada] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const lista = useContatos({ busca: buscaAtrasada || undefined, filtro });

  useEffect(() => {
    const t = setTimeout(() => setBuscaAtrasada(busca.trim()), 250);
    return () => clearTimeout(t);
  }, [busca]);

  return (
    <div className={`contatos ${id ? 'com-ficha' : ''}`}>
      <section className="contatos-lista" aria-label="Contatos">
        <header className="lista-topo">
          <div className="lista-titulo">
            <h1>Contatos</h1>
            <span className="contagem">{lista.data?.length ?? '–'}</span>
          </div>
          <label className="busca">
            <Search aria-hidden />
            <input
              id="busca-contatos"
              type="search"
              placeholder="Nome, empresa ou telefone"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            {busca && (
              <button
                className="icone-btn sem-borda"
                aria-label="Limpar busca"
                onClick={() => setBusca('')}
              >
                <X />
              </button>
            )}
          </label>
        </header>
        <nav className="abas" role="tablist" aria-label="Filtro">
          {(
            [
              ['todos', 'Todos'],
              ['recorrentes', 'Recorrentes'],
              ['bloqueados', 'Bloqueados'],
            ] as const
          ).map(([k, r]) => (
            <button
              key={k}
              role="tab"
              aria-selected={filtro === k}
              className="aba"
              onClick={() => setFiltro(k)}
            >
              {r}
            </button>
          ))}
        </nav>
        <div className="lista-itens">
          {lista.data?.length === 0 && <p className="lista-vazia">Nenhum contato encontrado.</p>}
          {lista.data?.map((c) => (
            <ItemContato key={c.id} c={c} />
          ))}
        </div>
      </section>

      {id ? (
        <FichaContato key={id} id={id} eu={eu} />
      ) : (
        <main className="ficha-vazia">
          <Vazio icone={<Users className="grande" />} titulo="Selecione um contato">
            Aqui fica a ficha do cliente com todas as conversas, inclusive as finalizadas, os
            pedidos e as observações.
          </Vazio>
        </main>
      )}
    </div>
  );
}

function ItemContato({ c }: { c: ContatoResumoDTO }) {
  const detalhe = [c.empresa, c.cidade].filter(Boolean).join(' · ');
  return (
    <NavLink to={`/contatos/${c.id}`} className="item">
      <AvatarContato id={c.id} nome={c.nome} fotoUrl={c.fotoUrl} />
      <span className="item-meio">
        <span className="item-linha">
          <span className="item-nome">{c.nome}</span>
          <span className="item-hora">{c.ultimoContato ? quandoCurto(c.ultimoContato) : ''}</span>
        </span>
        <span className="item-linha">
          <span className="item-prev">
            {c.telefones[0] ? formatarTelefone(c.telefones[0]) : 'sem telefone'}
            {detalhe && ` · ${detalhe}`}
          </span>
          <span className="item-sinais">
            {c.bloqueado && <Ban aria-label="Bloqueado" className="ico-bloqueado" />}
          </span>
        </span>
        <span className="item-detalhes">
          {c.conversas} {c.conversas === 1 ? 'conversa' : 'conversas'}
          {c.recorrente && <span className="sep"> · </span>}
          {c.recorrente && 'recorrente'}
          {c.conversaAbertaId && <span className="sep"> · </span>}
          {c.conversaAbertaId && <span className="aberta">em atendimento</span>}
        </span>
      </span>
    </NavLink>
  );
}

function FichaContato({ id, eu }: { id: string; eu: AtendenteDTO }) {
  const contato = useContato(id);
  const qc = useQueryClient();
  const toast = useToast();
  const [modal, setModal] = useState<'editar' | 'juntar' | null>(null);
  const [obs, setObs] = useState('');
  const c = contato.data;

  const atualizar = () => qc.invalidateQueries({ queryKey: ['contatos'] });

  if (contato.isError) {
    return (
      <main className="ficha">
        <p className="lista-vazia">{(contato.error as Error).message}</p>
      </main>
    );
  }
  if (!c) return <main className="ficha" aria-busy />;

  async function salvarObs() {
    if (!obs.trim()) return;
    try {
      await post(`/api/contatos/${id}/observacoes`, { texto: obs.trim() });
      setObs('');
      atualizar();
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  async function apagarObs(obsId: string) {
    try {
      await api(`/api/contatos/observacoes/${obsId}`, { metodo: 'DELETE' });
      atualizar();
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  const conversaParaAbrir = c.conversaAbertaId ?? c.historico[0]?.id;

  return (
    <main className="ficha">
      <header className="ficha-topo">
        <Link to="/contatos" className="icone-btn voltar-contatos" aria-label="Voltar para a lista">
          <ArrowLeft />
        </Link>
        <AvatarContato id={c.id} nome={c.nome} fotoUrl={c.fotoUrl} tamanho={64} />
        <div className="ficha-quem">
          <h2>{c.nome}</h2>
          <p>
            {c.telefones.map((t) => formatarTelefone(t)).join(' · ') || 'sem telefone'}
            {(c.empresa || c.cidade) && ` · ${[c.empresa, c.cidade].filter(Boolean).join(' · ')}`}
          </p>
          <p className="ficha-tags">
            {c.recorrente && <span className="contato-tag">Cliente recorrente</span>}
            {c.bloqueado && <span className="bloqueado-tag">Bloqueado</span>}
            <span className="desde">Cliente desde {data.format(new Date(c.criadoEm))}</span>
          </p>
        </div>
        <div className="ficha-acoes">
          {conversaParaAbrir && (
            <Link className="btn primario" to={`/conversas/${conversaParaAbrir}`}>
              <MessageCircle /> {c.conversaAbertaId ? 'Abrir conversa' : 'Ver última conversa'}
            </Link>
          )}
          <button className="btn" onClick={() => setModal('editar')}>
            <Pencil /> Editar
          </button>
          {eu.papel === 'admin' && (
            <button className="btn" onClick={() => setModal('juntar')}>
              <Merge /> Juntar duplicado
            </button>
          )}
        </div>
      </header>

      <div className="ficha-corpo">
        <section className="sec">
          <h2>Conversas ({c.historico.length})</h2>
          {c.historico.length === 0 ? (
            <p className="nota-sec">Nenhuma conversa ainda.</p>
          ) : (
            <ol className="historico">
              {c.historico.map((h) => (
                <li key={h.id}>
                  <Link to={`/conversas/${h.id}`} className="historico-item">
                    <span className="historico-topo">
                      <StatusTexto status={h.status} />
                      <span className="historico-data">
                        {data.format(new Date(h.criadoEm))}
                        {h.finalizadoEm && ` → ${data.format(new Date(h.finalizadoEm))}`}
                      </span>
                    </span>
                    <span className="historico-texto">
                      {h.resumo ?? h.ultimaMensagem ?? ROTULO_STATUS[h.status]}
                    </span>
                    <span className="historico-pe">
                      {ORIGEM[h.origem]}
                      {h.atendente && ` · ${h.atendente}`}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </section>

        <div className="ficha-lado">
          <section className="sec">
            <h2>Pedidos ({c.pedidos.length})</h2>
            {c.pedidos.length === 0 ? (
              <p className="nota-sec">Nenhum pedido.</p>
            ) : (
              <ul className="pedidos-lista">
                {c.pedidos.map((p) => (
                  <li key={p.id}>
                    <span className={`etapa-chip ${p.etapa === 'entregue' ? 'ok' : ''}`}>
                      {ROTULO_ETAPA[p.etapa]}
                    </span>
                    <span>{p.tinyNumero ? `Tiny #${p.tinyNumero}` : 'sem nº do Tiny'}</span>
                    <small>{data.format(new Date(p.criadoEm))}</small>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="sec">
            <h2>Observações do contato</h2>
            <div className="obs-campo">
              <textarea
                id="contato-obs"
                rows={2}
                value={obs}
                maxLength={2000}
                onChange={(e) => setObs(e.target.value)}
                placeholder="Vale pra todas as conversas deste cliente"
              />
              {obs.trim() && (
                <div className="obs-acoes">
                  <button className="btn primario" onClick={() => void salvarObs()}>
                    Salvar
                  </button>
                </div>
              )}
            </div>
            {c.observacoes.length > 0 && (
              <ul className="obs-itens do-contato">
                {c.observacoes.map((o) => (
                  <li key={o.id}>
                    <div className="obs-topo">
                      <b>{o.autor}</b>
                      <small>{quandoCurto(o.criadoEm)}</small>
                      <button
                        className="icone-btn sem-borda"
                        aria-label="Apagar observação"
                        onClick={() => void apagarObs(o.id)}
                      >
                        <Trash2 />
                      </button>
                    </div>
                    <p>{o.texto}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="sec">
            <h2>Cadastro no Tiny</h2>
            <CadastroTiny contatoId={c.id} />
          </section>
        </div>
      </div>

      {modal === 'editar' && (
        <ModalEditar c={c} fechar={() => setModal(null)} aoSalvar={atualizar} />
      )}
      {modal === 'juntar' && <ModalJuntar c={c} fechar={() => setModal(null)} />}
    </main>
  );
}

function ModalEditar({
  c,
  fechar,
  aoSalvar,
}: {
  c: ContatoDetalheDTO;
  fechar: () => void;
  aoSalvar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  async function salvar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    try {
      await patch(`/api/contatos/${c.id}`, { nome: d.nome, empresa: d.empresa, cidade: d.cidade });
      toast('Contato atualizado');
      aoSalvar();
      qc.invalidateQueries({ queryKey: ['conversas'] });
      fechar();
    } catch (err) {
      setErro((err as Error).message);
    }
  }

  return (
    <Modal titulo="Editar contato" aoFechar={fechar}>
      <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label className="campo">
          <span>Nome</span>
          <input
            id="ct-nome"
            name="nome"
            className="input"
            defaultValue={c.nome}
            required
            maxLength={120}
          />
        </label>
        <label className="campo">
          <span>Empresa</span>
          <input
            id="ct-empresa"
            name="empresa"
            className="input"
            defaultValue={c.empresa ?? ''}
            maxLength={120}
          />
        </label>
        <label className="campo">
          <span>Cidade</span>
          <input
            id="ct-cidade"
            name="cidade"
            className="input"
            defaultValue={c.cidade ?? ''}
            maxLength={120}
          />
          <small>Muda só na central. O cadastro oficial é o do Tiny (Fase 5).</small>
        </label>
        {erro && <div className="erro-form">{erro}</div>}
        <div className="acoes">
          <button type="button" className="btn" onClick={fechar}>
            Cancelar
          </button>
          <button className="btn primario">Salvar</button>
        </div>
      </form>
    </Modal>
  );
}

function ModalJuntar({ c, fechar }: { c: ContatoDetalheDTO; fechar: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [busca, setBusca] = useState('');
  const [escolhido, setEscolhido] = useState<ContatoResumoDTO | null>(null);
  const [salvando, setSalvando] = useState(false);
  const resultado = useContatos({ busca: busca.trim().length >= 2 ? busca.trim() : undefined });
  const opcoes = (resultado.data ?? []).filter((x) => x.id !== c.id).slice(0, 8);

  async function juntar() {
    if (!escolhido) return;
    setSalvando(true);
    try {
      await post(`/api/contatos/${c.id}/juntar`, { outroId: escolhido.id });
      qc.invalidateQueries({ queryKey: ['contatos'] });
      qc.invalidateQueries({ queryKey: ['conversas'] });
      toast('Contatos juntados');
      fechar();
      navegar(`/contatos/${c.id}`, { replace: true });
    } catch (e) {
      toast((e as Error).message, 'erro');
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Juntar contato duplicado" aoFechar={fechar}>
      <p style={{ margin: 0 }}>
        Escolha o cadastro repetido. Telefones, conversas, pedidos e observações dele passam pra{' '}
        <b>{c.nome}</b>, e o repetido some.
      </p>
      <label className="busca">
        <Search aria-hidden />
        <input
          id="juntar-busca"
          type="search"
          placeholder="Buscar o contato repetido"
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setEscolhido(null);
          }}
          autoFocus
        />
      </label>
      <div className="juntar-lista" role="radiogroup" aria-label="Contato repetido">
        {opcoes.map((o) => (
          <button
            key={o.id}
            role="radio"
            aria-checked={escolhido?.id === o.id}
            className="transferir-opcao"
            onClick={() => setEscolhido(o)}
          >
            <AvatarContato id={o.id} nome={o.nome} fotoUrl={o.fotoUrl} tamanho={32} />
            <span>
              <b>{o.nome}</b>
              <small>
                {o.telefones.map((t) => formatarTelefone(t)).join(' · ')} · {o.conversas}{' '}
                conversa(s)
              </small>
            </span>
          </button>
        ))}
        {busca.trim().length >= 2 && opcoes.length === 0 && (
          <p className="nota-sec">Nenhum outro contato com esse nome.</p>
        )}
      </div>
      <div className="acoes">
        <button className="btn" onClick={fechar}>
          Cancelar
        </button>
        <button
          className="btn perigo-cheio"
          disabled={!escolhido || salvando}
          onClick={() => void juntar()}
        >
          Juntar contatos
        </button>
      </div>
    </Modal>
  );
}
